import * as Sentry from '@sentry/react';
import { QueryErrorResetBoundary, useQuery, useSuspenseQueries } from '@tanstack/react-query';
import { atom, useAtom } from 'jotai';
import { atomFamily } from 'jotai-family';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import {
    checkIsFreeProductKey,
    getFeatureDefinition,
    getMaxMembersLimit,
    getSiteProductDefinition,
    hasAccessToFeature,
    PRICING_FEATURES,
    SITE_FEATURES,
    SITE_FEATURES_MAP,
} from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';
import {
    AtomButton,
    Badge,
    Button,
    Description,
    Dialog,
    DialogActionButton,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogFooterSecondary,
    DialogHeader,
    DialogRoot,
    Disclosure,
    DisclosureChevron,
    DisclosurePanel,
    DisclosureTrigger,
    Heading,
    type IconDefinition,
    intlFormatList,
    intlPluralizeString,
    LabeledContainer,
    LabeledContainerBody,
    List,
    ListItem,
    ListItemBody,
    ListItemDescription,
    ListItemDetail,
    ListItemLeading,
    ListItemTrailing,
    LoadingPanel,
    Pagination,
    ProgressiveImage,
    renderIcon,
    toast,
    usePaginatedList,
} from '@gitbook/spine-react';

import { useNavigationState, useSetAnalyticsScreen } from '../../navigation';
import {
    apiClient,
    apiQuery,
    apiResource,
    assertOrganizationWithBilling,
    ContactSupportReasons,
    useContactSupport,
    useInfiniteAPIQuery,
} from '../../state';
import { UpgradeDialogProvider, useUpgradeDialogContext } from '../UpgradeDialog';
import { trialAssets } from './assets';
import { flattenSpacesInOrgAllContent } from './content';

const MEMBERS_PER_PAGE = 5;
const SITES_PER_PAGE = 3;

const trialEndedDialogOpenAtom = atomFamily((_trial: string) => atom(true));

type TrialEndedAction = Extract<
    Parameters<typeof analytics.logEvent>[0],
    { type: 'organization_end_of_trial_action' }
>['action'];

/** Show the trial-end dialog without blocking the organization screen. */
export function TrialEndedDialog() {
    const { screen } = useNavigationState();
    const organizationId = 'organization' in screen ? screen.organization : undefined;
    if (!organizationId) {
        return null;
    }
    return (
        <UpgradeDialogProvider key={organizationId}>
            <OrganizationTrialEndedDialog organizationId={organizationId} />
        </UpgradeDialogProvider>
    );
}

/**
 * Whether a Free organization still has an expired trial for the current user to resolve.
 * Only admins can resolve it; other members are disabled on Free and see the lock screen instead.
 */
export function checkHasUnresolvedTrial(organization: api.Organization): boolean {
    return (
        organization.permissions.admin &&
        organization.trial.status === api.BillingTrialStatus.Expired &&
        !organization.trial.decision &&
        checkIsFreeProductKey(organization.plan)
    );
}

/** Temporary dialog visibility; the saved trial decision controls permanent dismissal. */
export function useTrialEndedDialog(organization: api.Organization) {
    const [isOpen, setIsOpen] = useAtom(
        trialEndedDialogOpenAtom(
            `${organization.id}:${organization.trial.count}:${organization.trial.endDate ?? ''}`
        )
    );
    return { isOpen, setIsOpen };
}

function OrganizationTrialEndedDialog({ organizationId }: { organizationId: string }) {
    const { data: organization } = useQuery(
        apiQuery('getOrganizationById', { organizationId }, { nullOnError: true })
    );
    if (!organization || !checkHasUnresolvedTrial(organization)) {
        return null;
    }
    return <OrganizationTrialEndedDialogRoot organization={organization} />;
}

function OrganizationTrialEndedDialogRoot({ organization }: { organization: api.Organization }) {
    const { isOpen, setIsOpen } = useTrialEndedDialog(organization);
    return (
        <DialogRoot
            isOpen={isOpen}
            onOpenChange={(open) => {
                if (!open) {
                    logTrialEndedAction(organization.id, 'close');
                }
                setIsOpen(open);
            }}
        >
            <TrialEndedDialogContent organization={organization} />
        </DialogRoot>
    );
}

function TrialEndedDialogContent({ organization }: { organization: api.Organization }) {
    const { openUpgradeDialog } = useUpgradeDialogContext();
    return (
        <Dialog size={{ width: 600 }}>
            <TrackTrialEndedDialogScreen organizationId={organization.id} />
            <DialogHeader className="gap-6 pt-4 md:pt-4">
                <ProgressiveImage
                    src={trialAssets.upgrade}
                    alt=""
                    className="h-[245px] w-full rounded-lg border border-neutral-subtle object-cover"
                />
                <Heading>Your trial has ended. Here's what changed.</Heading>
                <Description>
                    You're now on the Free plan, with some features disabled. You can stay on Free
                    as long as you like, or upgrade anytime to regain access to paid features.
                </Description>
            </DialogHeader>
            <DialogBody>
                <LabeledContainerBody>
                    <QueryErrorResetBoundary key={organization.id}>
                        {({ reset }) => (
                            <Sentry.ErrorBoundary
                                onReset={reset}
                                fallback={({ resetError }) => (
                                    <TrialEndedChangesError
                                        organizationId={organization.id}
                                        onRetry={resetError}
                                    />
                                )}
                            >
                                <React.Suspense fallback={<LoadingPanel />}>
                                    <TrialEndedChanges organization={organization} />
                                </React.Suspense>
                            </Sentry.ErrorBoundary>
                        )}
                    </QueryErrorResetBoundary>
                </LabeledContainerBody>
            </DialogBody>
            <DialogFooter>
                <DialogFooterSecondary>
                    <DialogCloseButton label="Close" />
                </DialogFooterSecondary>
                <DialogActionButton
                    label="Stay on Free"
                    kind="link"
                    onPress={async (_event, { preventClose }) => {
                        logTrialEndedAction(organization.id, 'stay_on_free');
                        await toast.catch(
                            async () => {
                                assertOrganizationWithBilling(organization);
                                await apiClient.billing.upgradeOrganizationPlan(organization.id, {
                                    product: organization.plan,
                                    interval: organization.billing.interval,
                                    reason: 'end_trial',
                                });
                            },
                            'Failed to save your trial decision',
                            { onReject: preventClose }
                        );
                    }}
                />
                <DialogActionButton
                    label="Upgrade"
                    kind="upgrade"
                    leadingIcon={Icon.Upgrade}
                    onPress={(_event, { preventClose }) => {
                        preventClose();
                        logTrialEndedAction(organization.id, 'upgrade');
                        openUpgradeDialog({
                            organization,
                            targetProduct: api.BillingProduct.Pro2024,
                            reason: 'end_trial',
                        });
                    }}
                />
            </DialogFooter>
        </Dialog>
    );
}

/** Track the screen from inside the modal, which is only mounted while the dialog is visible. */
function TrackTrialEndedDialogScreen({ organizationId }: { organizationId: string }) {
    useSetAnalyticsScreen(
        analytics.createOrgEvent(organizationId, { screen: 'organization_end_of_trial' }),
        [organizationId]
    );
    return null;
}

function logTrialEndedAction(organizationId: string, action: TrialEndedAction) {
    analytics.logEvent(
        analytics.createOrgEvent(organizationId, {
            type: 'organization_end_of_trial_action',
            action,
        })
    );
}

function TrialEndedChangesError({
    organizationId,
    onRetry,
}: {
    organizationId: string;
    onRetry: () => void;
}) {
    const contactSupport = useContactSupport();

    return (
        <div className="flex flex-col items-center gap-3 py-6 text-center" role="alert">
            <Description>
                We couldn't load what changed. If the issue persists, please contact support.
            </Description>
            <div className="flex gap-2">
                <Button
                    label="Retry"
                    onPress={() => {
                        logTrialEndedAction(organizationId, 'retry');
                        onRetry();
                    }}
                />
                <Button
                    label="Contact support"
                    kind="secondary"
                    loadable
                    onPress={() => {
                        logTrialEndedAction(organizationId, 'contact_support');
                        return contactSupport({ reason: ContactSupportReasons.BILLING_ISSUE });
                    }}
                />
            </div>
        </div>
    );
}

function TrialEndedChanges({ organization }: { organization: api.Organization }) {
    const [{ data: content }, { data: usage }] = useSuspenseQueries({
        queries: [
            apiQuery('getOrganizationAllContent', { organizationId: organization.id }),
            apiQuery('getUsageForOrganizationById', { organizationId: organization.id }),
        ],
    });
    const maxMembers = getMaxMembersLimit(organization.plan);
    const disabledMembersCount = usage.members.disabled.billing;
    const enabledAIFeatures = PRICING_FEATURES.filter(
        ({ key, aiFeature }) =>
            organization.aiPreference &&
            key !== api.OrganizationFeature.MaxMembers &&
            !hasAccessToFeature(organization.plan, key) &&
            aiFeature &&
            organization.aiSettings?.[aiFeature] !== false
    ).map(({ key }) => key);
    const shouldLoadTranslations = enabledAIFeatures.includes(api.OrganizationFeature.Translations);
    const {
        data: translations,
        isPending: translationsPending,
        error: translationsError,
    } = useQuery({
        ...apiQuery('listTranslations', { organizationId: organization.id, limit: 1 }),
        enabled: shouldLoadTranslations,
    });
    const aiFeatures = enabledAIFeatures.filter(
        (key) => key !== api.OrganizationFeature.Translations || (translations?.count ?? 0) > 0
    );
    const mergeRules = [
        organization.mergeRules,
        ...flattenSpacesInOrgAllContent(content).map((space) => space.mergeRules),
    ];
    const proFeatureCounts: Partial<
        Record<api.OrganizationFeature, { total: number; unit: string }>
    > = {
        [api.OrganizationFeature.ChangeRequestsMergeRules]: {
            total: mergeRules.reduce(
                (total, rules) => total + (rules.type === 'rules' ? rules.rules.length : 0),
                0
            ),
            unit: 'rule',
        },
        [api.OrganizationFeature.MembersTeams]: { total: usage.teams.total, unit: 'team' },
    };
    const proFeatures = PRICING_FEATURES.filter(
        ({ key, showInTrialEndSummary }) =>
            showInTrialEndSummary &&
            key !== api.OrganizationFeature.MaxMembers &&
            !hasAccessToFeature(organization.plan, key) &&
            (key === api.OrganizationFeature.PdfExport ||
                (key === api.OrganizationFeature.MembersAdvancedRoles
                    ? usage.members.review + usage.members.comment > 0
                    : (proFeatureCounts[key]?.total ?? 0) > 0))
    ).map(({ key }) => key);
    const sites = React.useMemo(
        () =>
            content.sites.filter(
                ({ site }) =>
                    (site.published &&
                        site.type !== api.SiteType.Basic &&
                        site.appliedType === api.SiteType.Basic) ||
                    (!site.published && site.unpublishedBy === 'system')
            ),
        [content.sites]
    );
    const {
        items: paginatedSites,
        paginationProps: sitesPagination,
        ref: sitesRef,
    } = usePaginatedList({ items: sites, itemsPerPage: SITES_PER_PAGE, label: 'sites' });
    const rows = [
        ...(disabledMembersCount
            ? [
                  {
                      id: 'members' as const,
                      icon: Icon.Users,
                      title: 'Members',
                      status: `${disabledMembersCount} disabled`,
                      content: (
                          <>
                              <Description>
                                  Free includes {maxMembers}{' '}
                                  {intlPluralizeString({
                                      count: maxMembers,
                                      singular: 'member',
                                      plural: 'members',
                                  })}
                                  . These members can no longer access{' '}
                                  <strong>{organization.title}</strong>. Their accounts will be
                                  re-enabled after upgrading.
                              </Description>
                              <DisabledTrialMembers
                                  organizationId={organization.id}
                                  disabledMembersCount={disabledMembersCount}
                              />
                          </>
                      ),
                  },
              ]
            : []),
        ...(aiFeatures.length
            ? [
                  {
                      id: 'ai' as const,
                      icon: Icon.AI,
                      title: 'AI features',
                      status: 'Turned off',
                      content: (
                          <>
                              <Description>
                                  AI features aren't included in the Free plan and have been
                                  disabled. Upgrade to re-enable them.
                              </Description>
                              <OrganizationFeaturesList keys={aiFeatures} />
                          </>
                      ),
                  },
              ]
            : []),
        ...(proFeatures.length
            ? [
                  {
                      id: 'pro' as const,
                      icon: Icon.Lock,
                      title: 'Pro features',
                      status: 'Turned off',
                      content: (
                          <>
                              <Description>
                                  Your existing content is unchanged, but the following features are
                                  now disabled. Upgrade to regain access.
                              </Description>
                              <OrganizationFeaturesList
                                  keys={proFeatures}
                                  counts={proFeatureCounts}
                              />
                          </>
                      ),
                  },
              ]
            : []),
    ];
    if (shouldLoadTranslations && translationsError) {
        throw translationsError;
    }
    if (shouldLoadTranslations && translationsPending) {
        return <LoadingPanel />;
    }
    return (
        <>
            {rows.length ? (
                <section aria-label="Organization">
                    <LabeledContainer level={3}>
                        <Heading className="text-ui-heading-group">Organization</Heading>
                        <List
                            items={rows}
                            compact
                            scrollable={false}
                            renderItem={({ item }) => (
                                <TrialChangeRow
                                    key={item.id}
                                    {...item}
                                    onExpandedChange={(isExpanded) =>
                                        analytics.logEvent(
                                            analytics.createOrgEvent(organization.id, {
                                                type: 'organization_end_of_trial_toggle_section',
                                                section: item.id,
                                                isExpanded,
                                            })
                                        )
                                    }
                                >
                                    {item.content}
                                </TrialChangeRow>
                            )}
                        />
                    </LabeledContainer>
                </section>
            ) : null}
            {sites.length ? (
                <section aria-label="Docs sites" className="mt-2">
                    <LabeledContainer level={3}>
                        <Heading className="text-ui-heading-group">Docs sites</Heading>
                        <List
                            ref={sitesRef}
                            items={paginatedSites}
                            compact
                            scrollable={false}
                            renderItem={({ item }) => (
                                <TrialSiteChange
                                    key={item.site.id}
                                    organizationId={organization.id}
                                    entry={item}
                                />
                            )}
                        />
                        {sites.length > SITES_PER_PAGE ? <Pagination {...sitesPagination} /> : null}
                    </LabeledContainer>
                </section>
            ) : null}
        </>
    );
}

function DisabledTrialMembers({
    organizationId,
    disabledMembersCount,
}: {
    organizationId: string;
    disabledMembersCount: number;
}) {
    const [page, setPage] = React.useState(0);
    const { items, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
        useInfiniteAPIQuery(
            apiResource('listMembersInOrganizationById', {
                organizationId,
                limit: MEMBERS_PER_PAGE,
            })
        );
    const members = items.filter((member) => member.disabled === api.MemberDisabledReason.Billing);
    const lastPage = Math.max(Math.ceil(disabledMembersCount / MEMBERS_PER_PAGE) - 1, 0);
    const currentPage = Math.min(page, lastPage);
    const requiredMembers = Math.min((currentPage + 1) * MEMBERS_PER_PAGE, disabledMembersCount);
    const needsMoreMembers = members.length < requiredMembers && hasNextPage;

    React.useEffect(() => {
        if (needsMoreMembers && !isLoading && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [needsMoreMembers, isLoading, isFetchingNextPage, fetchNextPage]);

    if (isLoading || isFetchingNextPage || needsMoreMembers) {
        return <LoadingPanel />;
    }
    return (
        <>
            <TrialChangesList
                items={members
                    .slice(currentPage * MEMBERS_PER_PAGE, (currentPage + 1) * MEMBERS_PER_PAGE)
                    .map((member) => ({
                        id: member.id,
                        title: member.user.displayName,
                        detail: member.user.email,
                    }))}
            />
            {disabledMembersCount > MEMBERS_PER_PAGE ? (
                <Pagination
                    range={{
                        totalItems: disabledMembersCount,
                        itemsPerPage: MEMBERS_PER_PAGE,
                        page: currentPage,
                        label: 'members',
                    }}
                    next={currentPage < lastPage ? () => setPage(currentPage + 1) : undefined}
                    previous={currentPage > 0 ? () => setPage(currentPage - 1) : undefined}
                />
            ) : null}
        </>
    );
}

function TrialSiteChange({
    organizationId,
    entry,
}: {
    organizationId: string;
    entry: api.OrganizationAllSite;
}) {
    const { site } = entry;
    const unpublished = !site.published;
    const unpublishFeatures = SITE_FEATURES.filter(
        ({ key, downgradeStrategy }) =>
            downgradeStrategy === 'unpublish' &&
            (site.features.some((feature) => feature.id === key) ||
                (key === api.SiteFeatureKey.SitesCustomSubdirectory && Boolean(site.proxy)))
    ).map(({ key }) => key);
    const unpublishReason = unpublishFeatures.some(
        (key) =>
            key === api.SiteFeatureKey.SitesVisitorAuthentication ||
            key === api.SiteFeatureKey.SitesShareLinks ||
            key === api.SiteFeatureKey.SitesUnlistedVisibility
    )
        ? "so sensitive content isn't exposed publicly"
        : unpublishFeatures.some(
                (key) =>
                    key === api.SiteFeatureKey.SitesCustomDomain ||
                    key === api.SiteFeatureKey.SitesCustomSubdirectory
            )
          ? 'instead of reverting to the gitbook.io domain'
          : unpublishFeatures.includes(api.SiteFeatureKey.SitesSections)
            ? 'to avoid displaying an incomplete site structure'
            : 'with its content and settings preserved';
    const lostFeatures = site.features.filter((feature) => feature.frozen);
    return (
        <TrialChangeRow
            icon={Icon.DocsSite}
            title={site.title}
            onExpandedChange={(isExpanded) =>
                analytics.logEvent(
                    analytics.createOrgEvent(organizationId, {
                        type: 'organization_end_of_trial_toggle_section',
                        section: 'site',
                        siteId: site.id,
                        isExpanded,
                    })
                )
            }
            subtitle={
                unpublished
                    ? getSiteProductDefinition(site.type).title
                    : `${getSiteProductDefinition(site.type).title} → Basic`
            }
            status={
                <Badge
                    label={unpublished ? 'Unpublished' : 'Moved to Basic'}
                    kind={unpublished ? 'danger' : 'warning'}
                    size="small"
                />
            }
        >
            {unpublished ? (
                <Description>
                    {site.title} uses{' '}
                    <strong>
                        {unpublishFeatures.length
                            ? intlFormatList(
                                  unpublishFeatures.map((key) => SITE_FEATURES_MAP[key].title)
                              )
                            : 'publishing settings'}
                    </strong>
                    , which {unpublishFeatures.length === 1 ? "isn't" : "aren't"} available on the
                    Free plan. We unpublished it {unpublishReason}. You can upgrade and republish it
                    with the same settings whenever you like.
                </Description>
            ) : (
                <>
                    <Description>
                        The site is still published, with Basic features only.
                        {lostFeatures.length ? ' These were turned off:' : null}
                    </Description>
                    {lostFeatures.length ? (
                        <TrialChangesList
                            items={lostFeatures.map((feature) => ({
                                id: feature.id,
                                title: SITE_FEATURES_MAP[feature.id].title,
                            }))}
                        />
                    ) : null}
                </>
            )}
        </TrialChangeRow>
    );
}

function TrialChangeRow({
    icon,
    title,
    subtitle,
    status,
    children,
    onExpandedChange,
}: {
    icon: IconDefinition;
    title: string;
    subtitle?: string;
    status: React.ReactNode;
    children: React.ReactNode;
    onExpandedChange: (isExpanded: boolean) => void;
}) {
    return (
        <ListItem kind="bordered" orientation="vertical" align="start" trimX trimY>
            <Disclosure onExpandedChange={onExpandedChange}>
                {({ isExpanded }) => (
                    <>
                        <DisclosureTrigger>
                            <AtomButton className="flex items-center gap-2 self-stretch text-left">
                                <ListItemLeading>{renderIcon(icon, { size: 16 })}</ListItemLeading>
                                <ListItemBody>
                                    <ListItemDescription>{title}</ListItemDescription>
                                    {subtitle ? <ListItemDetail>{subtitle}</ListItemDetail> : null}
                                </ListItemBody>
                                <ListItemTrailing when="always">{status}</ListItemTrailing>
                                <DisclosureChevron size={16} />
                            </AtomButton>
                        </DisclosureTrigger>
                        {isExpanded ? (
                            <DisclosurePanel className="self-stretch">
                                <LabeledContainerBody>{children}</LabeledContainerBody>
                            </DisclosurePanel>
                        ) : null}
                    </>
                )}
            </Disclosure>
        </ListItem>
    );
}

function OrganizationFeaturesList({
    keys,
    counts,
}: {
    keys: api.OrganizationFeature[];
    counts?: Partial<Record<api.OrganizationFeature, { total: number; unit: string }>>;
}) {
    return (
        <TrialChangesList
            items={keys.map((key) => {
                const count = counts?.[key];
                return {
                    id: key,
                    title:
                        key === api.OrganizationFeature.PdfExport
                            ? 'Internal PDF export'
                            : (getFeatureDefinition(key)?.title ?? key),
                    detail: count
                        ? `${count.total} ${count.unit}${count.total === 1 ? '' : 's'}`
                        : undefined,
                };
            })}
        />
    );
}

function TrialChangesList({ items }: { items: { id: string; title: string; detail?: string }[] }) {
    return (
        <List
            items={items}
            bordered
            compact
            scrollable={false}
            renderItem={({ item }) => (
                <ListItem key={item.id} kind="bordered">
                    <ListItemBody>
                        <ListItemDescription>{item.title}</ListItemDescription>
                    </ListItemBody>
                    {item.detail ? (
                        <ListItemTrailing when="always">{item.detail}</ListItemTrailing>
                    ) : null}
                </ListItem>
            )}
        />
    );
}
